import { api } from "@/services/api";

export interface DashboardOrder {
  appointmentId: number;
  userId: number;
  candidateName: string;
  employeeId?: string | null;
  visitorAddress?: string | null;
  visitorGender?: string | null;
  visitorZipCode?: string | null;
  visitorSsnMasked?: string | null;
  dateOfBirth: string;
  phoneNumber: string;
  email: string;
  orderId: number;
  orderNumber: string;
  orderStatus: string;
  orderDate: string;
  clinicId: number;
  clinicName: string;
  clinicAddress: string;
  clinicPhone?: string | null;
  testName: string;
  appointmentDate: string;
  visitDate: string;
  notes: string;
  clientId: number;
  clientName: string;
  vendorId: number;
  vendorName: string;
  orderSubmittedBy: string;
  totalCost?: number | null;
  currencyCode?: string | null;
}

export interface DashboardOrderTestDetail {
  appointmentId: number;
  clinicTestId: number;
  testTypeId: number;
  testName: string;
  aliasName?: string | null;
  appointmentStatus: string;
  scheduledAt: string;
  testPrice?: number | null;
  collectionFee?: number | null;
  currencyCode?: string | null;
  visitorTestId?: number | null;
  visitorTestStatus?: string | null;
  resultDocumentUrl?: string | null;
}

export interface DashboardOrderDetail {
  appointmentId: number;
  userId: number;
  candidateName: string;
  employeeId?: string | null;
  visitorAddress?: string | null;
  visitorGender?: string | null;
  visitorZipCode?: string | null;
  visitorSsnMasked?: string | null;
  dateOfBirth: string;
  phoneNumber: string;
  email: string;
  orderStatus: string;
  orderNumber?: string | null;
  orderDate: string;
  orderSubmittedBy: string;
  clinicId: number;
  clinicName: string;
  clinicAddress: string;
  clinicPhone?: string | null;
  visitDate: string;
  notes: string;
  clientId: number;
  clientName: string;
  vendorId: number;
  vendorName: string;
  vendorContactName?: string | null;
  vendorPhoneNumber?: string | null;
  vendorEmailAddress?: string | null;
  vendorFullAddress?: string | null;
  vendorAppointmentRequirements?: string | null;
  vendorCocRequirements?: string | null;
  acceptsMedigenixForm?: boolean | null;
  collectionFee?: number | null;
  totalCost?: number | null;
  currencyCode?: string | null;
  tests: DashboardOrderTestDetail[];
}

export interface DashboardStats {
  activeOrders: number;
  inProgress: number;
  scheduledAppointments: number;
  completedToday: number;
}

export const dashboardApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getDashboardStats: builder.query<DashboardStats, { clientId?: number; role?: string; email?: string } | void>({
      query: (params) => {
        let url = "/dashboard/stats";
        const queryParams = new URLSearchParams();
        if (params) {
          if (params.clientId) queryParams.append("clientId", params.clientId.toString());
          if (params.role) queryParams.append("role", params.role);
          if (params.email) queryParams.append("email", params.email);
        }
        if (queryParams.toString()) url += `?${queryParams.toString()}`;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["Orders"],
    }),

    // Fetch all dashboard orders with optional filters
    getDashboardOrders: builder.query<DashboardOrder[], { clientId?: number; status?: string; role?: string; email?: string } | void>({
      query: (params) => {
        let url = "/dashboard/orders";
        const queryParams = new URLSearchParams();
        
        if (params) {
          if (params.clientId) queryParams.append("clientId", params.clientId.toString());
          if (params.status) queryParams.append("status", params.status);
          if (params.role) queryParams.append("role", params.role);
          if (params.email) queryParams.append("email", params.email);
        }
        
        if (queryParams.toString()) {
          url += `?${queryParams.toString()}`;
        }
        
        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["Orders"],
    }),

    getDashboardOrderDetails: builder.query<DashboardOrderDetail, { appointmentId: number; role?: string; clientId?: number }>({
      query: ({ appointmentId, role, clientId }) => {
        let url = `/dashboard/orders/${appointmentId}/details`;
        const queryParams = new URLSearchParams();
        if (role) queryParams.append("role", role);
        if (clientId) queryParams.append("clientId", clientId.toString());
        if (queryParams.toString()) url += `?${queryParams.toString()}`;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["Orders", "VisitorTests"],
    }),

    getDashboardOrderAuthorizationPdf: builder.query<Blob, number>({
      query: (appointmentId) => ({
        url: `/dashboard/orders/${appointmentId}/authorization-pdf`,
        method: "GET",
        responseHandler: async (response: Response) => response.blob(),
      }),
    }),

    // Cancel an appointment/order
    cancelOrder: builder.mutation<void, { appointmentId: number; role?: string; clientId?: number }>({
      query: ({ appointmentId, role, clientId }) => {
        let url = `/dashboard/orders/${appointmentId}/cancel`;
        const queryParams = new URLSearchParams();
        if (role) queryParams.append("role", role);
        if (clientId) queryParams.append("clientId", clientId.toString());
        if (queryParams.toString()) url += `?${queryParams.toString()}`;
        return {
          url,
          method: "PATCH",
        };
      },
      invalidatesTags: ["Orders"],
    }),

    // Reschedule an appointment
    rescheduleOrder: builder.mutation<void, { appointmentId: number; newDate: string; role?: string; clientId?: number }>({
      query: ({ appointmentId, newDate, role, clientId }) => {
        let url = `/dashboard/orders/${appointmentId}/reschedule`;
        const queryParams = new URLSearchParams();
        if (role) queryParams.append("role", role);
        if (clientId) queryParams.append("clientId", clientId.toString());
        if (queryParams.toString()) url += `?${queryParams.toString()}`;
        return {
          url,
          method: "PATCH",
          body: {
            appointmentDate: newDate,
          },
        };
      },
      invalidatesTags: ["Orders"],
    }),

    updateOrderStatus: builder.mutation<void, { appointmentId: number; status: string; role?: string; clientId?: number }>({
      query: ({ appointmentId, status, role, clientId }) => {
        let url = `/dashboard/orders/${appointmentId}/status`;
        const queryParams = new URLSearchParams();
        if (role) queryParams.append("role", role);
        if (clientId) queryParams.append("clientId", clientId.toString());
        if (queryParams.toString()) url += `?${queryParams.toString()}`;
        return {
          url,
          method: "PATCH",
          body: { status },
        };
      },
      invalidatesTags: ["Orders"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetDashboardStatsQuery,
  useGetDashboardOrdersQuery,
  useGetDashboardOrderDetailsQuery,
  useLazyGetDashboardOrderAuthorizationPdfQuery,
  useCancelOrderMutation,
  useRescheduleOrderMutation,
  useUpdateOrderStatusMutation,
} = dashboardApi;
